import { GoHome } from "react-icons/go";
import { LuBarChart } from "react-icons/lu";
import { VscGitPullRequestGoToChanges } from "react-icons/vsc";
import { IoMdCodeWorking } from "react-icons/io";
import { IoCodeSlashOutline } from "react-icons/io5";
import { BiBriefcase } from "react-icons/bi";
import { LuUser2 } from "react-icons/lu";
import { LiaUserLockSolid } from "react-icons/lia";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { MdOutlineNotificationsActive } from "react-icons/md";
import { GrCurrency } from "react-icons/gr";
import { IoSettingsOutline } from "react-icons/io5";
import { useLocation } from "react-router-dom";
import CryptoJS from "crypto-js";
import { createContext, useContext } from "react";
import { useMsal } from "@azure/msal-react";

export const flattenDataForExcel = (data) => {
  // Define the exact column order
  const columnOrder = [
    "empName",
    "empID",
    "natureOfExpense",
    "businessPurpose",
    "projectCodeID",
    "companyName",
    "expensePeriod",
    "month",
    "departmentID",
    "expenseCodeID",
    "currencyID",
    "cost",
    "totalReimbursement",
    "approvers",
    "adminApprover",
    "status",
    "paymentPaidOn",
  ];
  const result = [];
  data.forEach((item) => {
    const { key, id, children, ...filteredData } = item;

    // Reorder columns for the parent
    // Reorder parent row according to columnOrder
    const reorderedParent = columnOrder.reduce((acc, key) => {
      acc[key] = filteredData[key] || "-"; // Use empty string if the field is missing
      return acc;
    }, {});

    // Add the parent row with all values
    result.push(reorderedParent);

    // Add children rows with only relevant columns
    if (children) {
      children.forEach((child) => {
        const reorderedChild = columnOrder.reduce((acc, key) => {
          acc[key] = child[key] || ""; // Ensure child fields follow the same order
          return acc;
        }, {});
        result.push(reorderedChild);
      });
    }
  });
  return result;
};

export const sortData = (data) => {
  return data.sort((a, b) => {
    if (a.label < b.label) {
      return -1; // a comes before b
    }
    if (a.label > b.label) {
      return 1; // a comes after b
    }
    return 0; // a and b are equal
  });
};
export const paginationOptions = (storeTotalCount) => {
  if (typeof storeTotalCount !== "number" || storeTotalCount <= 0) {
    // Fallback to default pagination options if storeTotalCount is invalid
    return ["10", "20", "50", "100"];
  }
  return storeTotalCount > 100
    ? ["10", "20", "50", "100", `${storeTotalCount}`]
    : storeTotalCount < 10
    ? [`${storeTotalCount}`, "10", "20", "50", "100"]
    : (() => {
        const baseOptions = ["10", "20", "50", "100"];
        if (!baseOptions.includes(`${storeTotalCount}`)) {
          baseOptions.push(`${storeTotalCount}`);
        }
        return baseOptions.sort((a, b) => Number(a) - Number(b));
      })();
};
export const sideMenu = {
  SUPERADMIN: [
    { "/dashboard": { name: "Dashboard", icon: <GoHome /> } },
    { "/list-of-expense": { name: "List of Expense", icon: <LuBarChart /> } },
    {
      "/pending-requests": {
        name: "Pending Requests",
        icon: <VscGitPullRequestGoToChanges />,
        showCount: "yes",
      },
    },
    { "/project-code": { name: "Project Code", icon: <IoMdCodeWorking /> } },
    { "/expense-code": { name: "Expense Code", icon: <IoCodeSlashOutline /> } },
    {
      "/departments": {
        name: "Departments",
        icon: <HiOutlineBuildingOffice2 />,
      },
    },
    {
      "/currency": {
        name: "Currency",
        icon: <GrCurrency />,
      },
    },
    { "/roles": { name: "Roles", icon: <BiBriefcase /> } },
    { "/permissions": { name: "Permissions", icon: <LiaUserLockSolid /> } },
    { "/users": { name: "Users", icon: <LuUser2 /> } },
    {
      "/notifications": {
        name: "Notifications",
        icon: <MdOutlineNotificationsActive />,
      },
    },
    { "/settings": { name: "Settings", icon: <IoSettingsOutline /> } },
  ],
  ADMIN: [
    { "/dashboard": { name: "Dashboard", icon: <GoHome /> } },
    { "/list-of-expense": { name: "List of Expense", icon: <LuBarChart /> } },
    {
      "/pending-requests": {
        name: "Pending Requests",
        icon: <VscGitPullRequestGoToChanges />,
        showCount: "yes",
      },
    },
    { "/project-code": { name: "Project Code", icon: <IoMdCodeWorking /> } },
    { "/expense-code": { name: "Expense Code", icon: <IoCodeSlashOutline /> } },
    {
      "/departments": {
        name: "Departments",
        icon: <HiOutlineBuildingOffice2 />,
      },
    },
    {
      "/currency": {
        name: "Currency",
        icon: <GrCurrency />,
      },
    },
    {
      "/notifications": {
        name: "Notifications",
        icon: <MdOutlineNotificationsActive />,
      },
    },
  ],
  APPROVER: [
    { "/dashboard": { name: "Dashboard", icon: <GoHome /> } },
    { "/list-of-expense": { name: "List of Expense", icon: <LuBarChart /> } },
    {
      "/pending-requests": {
        name: "Pending Requests",
        icon: <VscGitPullRequestGoToChanges />,
        showCount: "yes",
      },
    },
    { "/project-code": { name: "Project Code", icon: <IoMdCodeWorking /> } },
    {
      "/notifications": {
        name: "Notifications",
        icon: <MdOutlineNotificationsActive />,
      },
    },
  ],
  EMPLOYEE: [
    { "/dashboard": { name: "Dashboard", icon: <GoHome /> } },
    { "/list-of-expense": { name: "List of Expense", icon: <LuBarChart /> } },
    { "/project-code": { name: "Project Code", icon: <IoMdCodeWorking /> } },
    {
      "/notifications": {
        name: "Notifications",
        icon: <MdOutlineNotificationsActive />,
      },
    },
  ],
};

/*Custom hook to call currentPath*/
export default function useCurrentPath() {
  const location = useLocation();
  return location.pathname.replace("/", "");
}

// Define a secret key for encryption/decryption
const SECRET_KEY = "@ex#mgtSys"; // Make sure to use a strong key

export const encryptData = (data) => {
  try {
    return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
  } catch (error) {
    console.error("Encryption failed:", error);
    return null;
  }
};

export const decryptData = (encryptedData) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
    return bytes && JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  } catch (error) {
    console.error("Decryption failed:", error);
    return null;
  }
};

const MsalContext = createContext();

export const MsalProvider = ({ children }) => {
  const msalInstance = useMsal();

  return (
    <MsalContext.Provider value={msalInstance}>{children}</MsalContext.Provider>
  );
};

export const useMsalInstance = () => {
  return useContext(MsalContext);
};
